/**
 * Mapa de engagement de la galería Cloudinary (sesión).
 *
 * Objetivo: que Kimi sepa **qué es “gallery”** para este visitante —
 * inventario de la carpeta/curación, cuánto recorrió, en qué subcarpetas,
 * y un score de “ganas de ver más” (intención visual).
 *
 * Flujo:
 *  1. BaseTemplate registra el catálogo al montar (`registerGalleryCatalog`).
 *  2. Cada open/swipe de foto Cloudinary actualiza el mapa.
 *  3. Al cruzar umbral → re-eval del orquestador de compra.
 */

import { BRAND_CLOUDINARY_FOLDER } from "@/lib/brandConfig";
import { cloudinaryPublicIdFromUrl } from "@/lib/photoTracking";
import { requestPurchaseEvalRefresh } from "@/lib/visitorPurchaseEvalStorage";

const STORAGE_KEY = "ph_gallery_engagement_v1";

export type GalleryFolderCount = {
  folder: string;
  count: number;
};

/** Snapshot rico para cliente + payload Kimi. */
export type GalleryEngagementSnapshot = {
  photo_opens: number;
  unique_cloudinary_photos: number;
  lightbox_swipes: number;
  reopens: number;
  /** Segundos totales mirando fotos en lightbox (Cloudinary). */
  total_dwell_seconds: number;
  /** Máximo segundos en una sola foto. */
  max_photo_dwell_seconds: number;
  /** Fotos con ≥3s de atención sostenida. */
  photos_long_dwell: number;
  /** Zoom / pinch / double-tap en lightbox (interés fuerte). */
  zoom_events: number;
  /** Intentos de descargar/guardar foto (botón + right-click + long-press). */
  download_intents: number;
  download_button_clicks: number;
  download_save_attempts: number;
  /** Sample public_ids donde quiso guardar. */
  download_sample_public_ids: string[];
  /** Fotos Cloudinary curadas en la home gallery (catálogo de sesión). */
  catalog_size: number;
  /** unique_viewed / catalog_size * 100 (0 si no hay catálogo). */
  coverage_pct: number;
  /** Carpetas Cloudinary tocadas (prefijos del public_id). */
  folders_touched: number;
  folder_breakdown: GalleryFolderCount[];
  /** Score 0–100: ganas de ver más / intención visual. */
  intent_score: number;
  /** Nivel legible para prompts y Slack. */
  intent_label: "none" | "curious" | "browsing" | "hungry" | "saturated";
  brand_folder: string;
  /** Sample de public_ids vistos (debug / Kimi, cap 12). */
  sample_public_ids: string[];
  /** Sample del catálogo (cap 12). */
  sample_catalog_ids: string[];
  updated_at: number;
};

type StoredGalleryMap = {
  photo_opens: number;
  unique_cloudinary_photos: number;
  lightbox_swipes: number;
  reopens: number;
  /** ms acumulados mirando lightbox Cloudinary. */
  total_dwell_ms: number;
  max_photo_dwell_ms: number;
  /** public_id → max dwell_ms en sesión */
  dwell_by_id: Record<string, number>;
  zoom_events: number;
  download_intents: number;
  download_button_clicks: number;
  download_save_attempts: number;
  download_public_ids: string[];
  updated_at: number;
  unique_ids: string[];
  /** public_id → opens en sesión */
  open_counts: Record<string, number>;
  catalog_ids: string[];
  brand_folder: string;
};

const LONG_DWELL_MS = 3000;

const DEFAULT_MIN_UNIQUE = 4;
const DEFAULT_MIN_OPENS = 5;
const MAX_IDS = 80;
const MAX_SAMPLE = 12;

function brandFolderDefault(): string {
  return (BRAND_CLOUDINARY_FOLDER || "").trim() || "unknown";
}

/**
 * Extrae carpeta Cloudinary del public_id.
 * `delfuocampo/portfolio/looks/foto01` → `delfuocampo/portfolio/looks`
 * `delfuocampo/home-gallery/a` → `delfuocampo/home-gallery`
 */
export function cloudinaryFolderFromPublicId(publicId: string): string {
  const id = publicId.replace(/^\/+/, "").trim();
  if (!id) return "(root)";
  const parts = id.split("/").filter(Boolean);
  if (parts.length <= 1) return "(root)";
  return parts.slice(0, -1).join("/");
}

/** Segmento de sección útil para Kimi (portfolio/looks, home-gallery, …). */
export function cloudinarySectionFromPublicId(publicId: string): string {
  const folder = cloudinaryFolderFromPublicId(publicId);
  if (folder === "(root)") return "root";
  const parts = folder.split("/").filter(Boolean);
  // brand/section/... → section/...
  if (parts.length >= 2) return parts.slice(1).join("/");
  return parts[0] || "root";
}

function emptyStored(): StoredGalleryMap {
  return {
    photo_opens: 0,
    unique_cloudinary_photos: 0,
    lightbox_swipes: 0,
    reopens: 0,
    total_dwell_ms: 0,
    max_photo_dwell_ms: 0,
    dwell_by_id: {},
    zoom_events: 0,
    download_intents: 0,
    download_button_clicks: 0,
    download_save_attempts: 0,
    download_public_ids: [],
    updated_at: 0,
    unique_ids: [],
    open_counts: {},
    catalog_ids: [],
    brand_folder: brandFolderDefault(),
  };
}

function readRaw(): StoredGalleryMap {
  if (typeof sessionStorage === "undefined") return emptyStored();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStored();
    const parsed = JSON.parse(raw) as Partial<StoredGalleryMap>;
    const uniqueIds = Array.isArray(parsed.unique_ids)
      ? parsed.unique_ids.slice(0, MAX_IDS)
      : [];
    const openCounts =
      parsed.open_counts && typeof parsed.open_counts === "object"
        ? (parsed.open_counts as Record<string, number>)
        : {};
    const dwellById =
      parsed.dwell_by_id && typeof parsed.dwell_by_id === "object"
        ? (parsed.dwell_by_id as Record<string, number>)
        : {};
    const catalogIds = Array.isArray(parsed.catalog_ids)
      ? parsed.catalog_ids.slice(0, MAX_IDS)
      : [];
    return {
      photo_opens: typeof parsed.photo_opens === "number" ? parsed.photo_opens : 0,
      unique_cloudinary_photos:
        typeof parsed.unique_cloudinary_photos === "number"
          ? parsed.unique_cloudinary_photos
          : uniqueIds.length,
      lightbox_swipes:
        typeof parsed.lightbox_swipes === "number" ? parsed.lightbox_swipes : 0,
      reopens: typeof parsed.reopens === "number" ? parsed.reopens : 0,
      total_dwell_ms:
        typeof parsed.total_dwell_ms === "number" ? parsed.total_dwell_ms : 0,
      max_photo_dwell_ms:
        typeof parsed.max_photo_dwell_ms === "number" ? parsed.max_photo_dwell_ms : 0,
      dwell_by_id: dwellById,
      zoom_events: typeof parsed.zoom_events === "number" ? parsed.zoom_events : 0,
      download_intents:
        typeof parsed.download_intents === "number" ? parsed.download_intents : 0,
      download_button_clicks:
        typeof parsed.download_button_clicks === "number"
          ? parsed.download_button_clicks
          : 0,
      download_save_attempts:
        typeof parsed.download_save_attempts === "number"
          ? parsed.download_save_attempts
          : 0,
      download_public_ids: Array.isArray(parsed.download_public_ids)
        ? parsed.download_public_ids.slice(0, MAX_SAMPLE)
        : [],
      updated_at: typeof parsed.updated_at === "number" ? parsed.updated_at : 0,
      unique_ids: uniqueIds,
      open_counts: openCounts,
      catalog_ids: catalogIds,
      brand_folder:
        typeof parsed.brand_folder === "string" && parsed.brand_folder
          ? parsed.brand_folder
          : brandFolderDefault(),
    };
  } catch {
    return emptyStored();
  }
}

function writeRaw(data: StoredGalleryMap): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    // Cap dwell map size
    const dwellEntries = Object.entries(data.dwell_by_id).slice(0, MAX_IDS);
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        photo_opens: data.photo_opens,
        unique_cloudinary_photos: data.unique_cloudinary_photos,
        lightbox_swipes: data.lightbox_swipes,
        reopens: data.reopens,
        total_dwell_ms: data.total_dwell_ms,
        max_photo_dwell_ms: data.max_photo_dwell_ms,
        dwell_by_id: Object.fromEntries(dwellEntries),
        zoom_events: data.zoom_events,
        download_intents: data.download_intents,
        download_button_clicks: data.download_button_clicks,
        download_save_attempts: data.download_save_attempts,
        download_public_ids: data.download_public_ids.slice(0, MAX_SAMPLE),
        updated_at: data.updated_at,
        unique_ids: data.unique_ids.slice(0, MAX_IDS),
        open_counts: data.open_counts,
        catalog_ids: data.catalog_ids.slice(0, MAX_IDS),
        brand_folder: data.brand_folder,
      }),
    );
  } catch {
    /* quota */
  }
}

function folderBreakdown(uniqueIds: string[]): GalleryFolderCount[] {
  const map = new Map<string, number>();
  for (const id of uniqueIds) {
    const section = cloudinarySectionFromPublicId(id);
    map.set(section, (map.get(section) || 0) + 1);
  }
  return [...map.entries()]
    .map(([folder, count]) => ({ folder, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);
}

/**
 * Score de “ganas de ver más” (0–100).
 * Combina profundidad (unique/opens), cobertura del catálogo y exploración multi-carpeta.
 */
export function computeGalleryIntentScore(input: {
  photo_opens: number;
  unique: number;
  swipes: number;
  reopens: number;
  catalog_size: number;
  folders_touched: number;
  /** Segundos totales mirando fotos (Cloudinary lightbox). */
  total_dwell_seconds?: number;
  max_photo_dwell_seconds?: number;
  photos_long_dwell?: number;
  zoom_events?: number;
  download_intents?: number;
}): number {
  const {
    photo_opens,
    unique,
    swipes,
    reopens,
    catalog_size,
    folders_touched,
    total_dwell_seconds = 0,
    max_photo_dwell_seconds = 0,
    photos_long_dwell = 0,
    zoom_events = 0,
    download_intents = 0,
  } = input;

  if (photo_opens <= 0 && unique <= 0 && total_dwell_seconds <= 0) return 0;

  let score = 8;
  score += Math.min(unique, 12) * 6; // +72 max
  score += Math.min(Math.max(photo_opens - unique, 0), 10) * 2; // reopens-ish
  score += Math.min(swipes, 15) * 1.5;
  score += Math.min(reopens, 8) * 2.5;
  score += Math.min(Math.max(folders_touched - 1, 0), 4) * 5;

  // Atención sostenida en fotos Cloudinary (tiempo > clicks)
  score += Math.min(total_dwell_seconds, 90) * 0.35; // +31.5 max
  score += Math.min(max_photo_dwell_seconds, 30) * 0.4; // +12 max
  score += Math.min(photos_long_dwell, 8) * 3; // +24 max
  // Zoom = intención de ver detalle (muy correlacionado con “quiero más”)
  score += Math.min(zoom_events, 10) * 4; // +40 max
  // Download/save intent = “quiero quedarme con esto” (muy fuerte)
  score += Math.min(download_intents, 8) * 6; // +48 max

  if (catalog_size > 0) {
    const coverage = unique / catalog_size;
    score += coverage * 28;
    if (coverage >= 0.7 && unique >= 3) score += 10;
    if (coverage >= 0.9 && unique >= 4) score += 8;
  } else if (unique >= 4) {
    score += 12; // sin catálogo: bonus por volumen
  }

  // saturación: abrió casi todo y sigue — alta gana de contenido exclusivo
  if (catalog_size > 0 && unique >= catalog_size && photo_opens >= catalog_size + 2) {
    score += 12;
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

export function galleryIntentLabel(
  score: number,
): GalleryEngagementSnapshot["intent_label"] {
  if (score <= 0) return "none";
  if (score < 25) return "curious";
  if (score < 50) return "browsing";
  if (score < 75) return "hungry";
  return "saturated";
}

function toSnapshot(raw: StoredGalleryMap): GalleryEngagementSnapshot {
  const unique = raw.unique_ids.length || raw.unique_cloudinary_photos;
  const catalogSize = raw.catalog_ids.length;
  const coverage =
    catalogSize > 0
      ? Math.min(100, Math.round((unique / catalogSize) * 100))
      : 0;
  const folders = folderBreakdown(raw.unique_ids);
  const totalDwellSec = Math.round((raw.total_dwell_ms || 0) / 1000);
  const maxDwellSec = Math.round((raw.max_photo_dwell_ms || 0) / 1000);
  const photosLong = Object.values(raw.dwell_by_id || {}).filter(
    (ms) => ms >= LONG_DWELL_MS,
  ).length;
  const downloadIntents = raw.download_intents || 0;
  const intent = computeGalleryIntentScore({
    photo_opens: raw.photo_opens,
    unique,
    swipes: raw.lightbox_swipes,
    reopens: raw.reopens,
    catalog_size: catalogSize,
    folders_touched: folders.length,
    total_dwell_seconds: totalDwellSec,
    max_photo_dwell_seconds: maxDwellSec,
    photos_long_dwell: photosLong,
    zoom_events: raw.zoom_events || 0,
    download_intents: downloadIntents,
  });

  return {
    photo_opens: raw.photo_opens,
    unique_cloudinary_photos: unique,
    lightbox_swipes: raw.lightbox_swipes,
    reopens: raw.reopens,
    total_dwell_seconds: totalDwellSec,
    max_photo_dwell_seconds: maxDwellSec,
    photos_long_dwell: photosLong,
    zoom_events: raw.zoom_events || 0,
    download_intents: downloadIntents,
    download_button_clicks: raw.download_button_clicks || 0,
    download_save_attempts: raw.download_save_attempts || 0,
    download_sample_public_ids: (raw.download_public_ids || []).slice(0, MAX_SAMPLE),
    catalog_size: catalogSize,
    coverage_pct: coverage,
    folders_touched: folders.length,
    folder_breakdown: folders,
    intent_score: intent,
    intent_label: galleryIntentLabel(intent),
    brand_folder: raw.brand_folder || brandFolderDefault(),
    sample_public_ids: raw.unique_ids.slice(0, MAX_SAMPLE),
    sample_catalog_ids: raw.catalog_ids.slice(0, MAX_SAMPLE),
    updated_at: raw.updated_at,
  };
}

export function getGalleryEngagementSnapshot(): GalleryEngagementSnapshot {
  return toSnapshot(readRaw());
}

/**
 * Registra el inventario Cloudinary de la galería home (site-settings / curación).
 * Debe llamarse cuando BaseTemplate resuelve `activeGalleryMedia`.
 */
export function registerGalleryCatalog(
  sources: Array<string | undefined | null>,
  opts?: { brandFolder?: string },
): GalleryEngagementSnapshot {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const src of sources) {
    if (!src) continue;
    const id = cloudinaryPublicIdFromUrl(src);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= MAX_IDS) break;
  }

  const before = readRaw();
  const next: StoredGalleryMap = {
    ...before,
    catalog_ids: ids,
    brand_folder: opts?.brandFolder?.trim() || before.brand_folder || brandFolderDefault(),
    updated_at: Date.now(),
  };
  writeRaw(next);
  return toSnapshot(next);
}

/**
 * Quality engagement: profundidad real (no solo 1–2 peeks).
 * 1–2 fotos sin dwell/zoom/reopen/download = normal (no candidato).
 */
export function hasGalleryQualityEngagement(
  s: Pick<
    GalleryEngagementSnapshot,
    | "unique_cloudinary_photos"
    | "photo_opens"
    | "total_dwell_seconds"
    | "max_photo_dwell_seconds"
    | "photos_long_dwell"
    | "zoom_events"
    | "reopens"
    | "download_intents"
  >,
): boolean {
  const unique = s.unique_cloudinary_photos;
  const opens = s.photo_opens;
  const volume = unique >= 3 || opens >= 4;
  const depth =
    s.total_dwell_seconds >= 12 ||
    s.max_photo_dwell_seconds >= 6 ||
    s.zoom_events >= 1 ||
    s.reopens >= 1 ||
    s.photos_long_dwell >= 2 ||
    s.download_intents >= 1;
  // Download + al menos 2 unique = quality aunque volumen bajo
  if (s.download_intents >= 1 && unique >= 2) return true;
  // Multi-download en una sola foto aún es señal fuerte
  if (s.download_intents >= 2) return true;
  return volume && depth;
}

export function isGalleryPremiumRouteCandidate(
  minUnique = DEFAULT_MIN_UNIQUE,
  minOpens = DEFAULT_MIN_OPENS,
): boolean {
  const s = getGalleryEngagementSnapshot();
  // Casual 1–2 fotos sin interacción real → no candidato
  if (!hasGalleryQualityEngagement(s) && s.download_intents < 1) {
    if (s.unique_cloudinary_photos <= 2 && s.photo_opens < minOpens) {
      return false;
    }
  }
  if (s.unique_cloudinary_photos >= minUnique || s.photo_opens >= minOpens) {
    return hasGalleryQualityEngagement(s) || s.unique_cloudinary_photos >= minUnique;
  }
  // Cobertura alta: exige unique ≥ 3 + quality
  if (
    s.catalog_size >= 3 &&
    s.coverage_pct >= 60 &&
    s.unique_cloudinary_photos >= 3 &&
    hasGalleryQualityEngagement(s)
  ) {
    return true;
  }
  // Dwell fuerte solo si hay algo de volumen
  if (
    (s.total_dwell_seconds >= 20 || s.max_photo_dwell_seconds >= 10) &&
    s.unique_cloudinary_photos >= 2
  ) {
    return true;
  }
  if (s.photos_long_dwell >= 3 && s.unique_cloudinary_photos >= 2) return true;
  if (s.zoom_events >= 1 && (s.total_dwell_seconds >= 8 || s.unique_cloudinary_photos >= 2)) {
    return true;
  }
  // Download intent is first-class
  if (s.download_intents >= 1 && s.unique_cloudinary_photos >= 2) return true;
  if (s.download_intents >= 2) return true;
  if (s.intent_score >= 55 && (s.unique_cloudinary_photos >= 3 || s.download_intents >= 1)) {
    return true;
  }
  return false;
}

/**
 * Registra apertura/swipe de foto Cloudinary en la galería/lightbox.
 * Dispara re-eval Kimi al cruzar umbral por primera vez en la sesión.
 */
export function recordGalleryCloudinaryView(
  src: string,
  opts?: { swipe?: boolean },
): GalleryEngagementSnapshot {
  const publicId = cloudinaryPublicIdFromUrl(src);
  if (!publicId) return getGalleryEngagementSnapshot();

  const before = readRaw();
  const wasCandidate = isGalleryPremiumRouteCandidateFromRaw(before);

  const uniqueIds = new Set(before.unique_ids);
  const alreadySeen = uniqueIds.has(publicId);
  uniqueIds.add(publicId);

  const openCounts = { ...before.open_counts };
  const prevOpens = openCounts[publicId] || 0;
  openCounts[publicId] = prevOpens + 1;

  const next: StoredGalleryMap = {
    ...before,
    photo_opens: before.photo_opens + 1,
    unique_cloudinary_photos: uniqueIds.size,
    lightbox_swipes: before.lightbox_swipes + (opts?.swipe ? 1 : 0),
    reopens: before.reopens + (alreadySeen ? 1 : 0),
    updated_at: Date.now(),
    unique_ids: [...uniqueIds].slice(0, MAX_IDS),
    open_counts: openCounts,
  };
  writeRaw(next);

  const snapshot = toSnapshot(next);
  const isCandidate = isGalleryPremiumRouteCandidate();
  if (isCandidate && !wasCandidate) {
    requestPurchaseEvalRefresh("gallery_engagement_threshold");
  }

  return snapshot;
}

/**
 * Registra cuánto tiempo miró una foto Cloudinary en lightbox.
 * Llamar al cerrar o al swipear (antes de resetear el timer).
 * El dwell enriquece intent_score y el payload a Kimi.
 */
export function recordGalleryPhotoDwell(
  src: string,
  dwellSeconds: number,
): GalleryEngagementSnapshot {
  const publicId = cloudinaryPublicIdFromUrl(src);
  if (!publicId) return getGalleryEngagementSnapshot();

  const sec = Math.max(0, Math.min(Math.floor(dwellSeconds), 600));
  if (sec < 1) return getGalleryEngagementSnapshot();

  const ms = sec * 1000;
  const before = readRaw();
  const wasCandidate = isGalleryPremiumRouteCandidateFromRaw(before);

  const dwellById = { ...before.dwell_by_id };
  const prevMax = dwellById[publicId] || 0;
  // Acumula dwell de re-aperturas de la misma foto
  dwellById[publicId] = prevMax + ms;

  const next: StoredGalleryMap = {
    ...before,
    total_dwell_ms: before.total_dwell_ms + ms,
    max_photo_dwell_ms: Math.max(before.max_photo_dwell_ms, dwellById[publicId]),
    dwell_by_id: dwellById,
    updated_at: Date.now(),
  };
  writeRaw(next);

  const snapshot = toSnapshot(next);
  // Dwell largo puede empujar a candidato premium sin más clicks
  if (isGalleryPremiumRouteCandidate() && !wasCandidate) {
    requestPurchaseEvalRefresh("gallery_dwell_threshold");
  } else if (sec >= 8) {
    // Atención fuerte en una foto → re-eval (interés real)
    requestPurchaseEvalRefresh("gallery_long_dwell");
  }

  return snapshot;
}

/**
 * Zoom / pinch / double-tap en una foto Cloudinary del lightbox.
 * Señal fuerte de “quiero ver más detalle” → empuja a premium.
 */
export function recordGalleryZoom(opts?: { source?: string }): GalleryEngagementSnapshot {
  const before = readRaw();
  const wasCandidate = isGalleryPremiumRouteCandidateFromRaw(before);
  const next: StoredGalleryMap = {
    ...before,
    zoom_events: before.zoom_events + 1,
    updated_at: Date.now(),
  };
  writeRaw(next);
  const snapshot = toSnapshot(next);
  if (isGalleryPremiumRouteCandidate() && !wasCandidate) {
    requestPurchaseEvalRefresh("gallery_zoom_threshold");
  } else if ((opts?.source || next.zoom_events) && next.zoom_events >= 1) {
    requestPurchaseEvalRefresh("gallery_zoom");
  }
  return snapshot;
}

function isGalleryPremiumRouteCandidateFromRaw(raw: StoredGalleryMap): boolean {
  // Reuse public logic via snapshot
  const s = toSnapshot(raw);
  if (!hasGalleryQualityEngagement(s) && s.download_intents < 1) {
    if (s.unique_cloudinary_photos <= 2 && s.photo_opens < DEFAULT_MIN_OPENS) {
      return false;
    }
  }
  if (s.unique_cloudinary_photos >= DEFAULT_MIN_UNIQUE || s.photo_opens >= DEFAULT_MIN_OPENS) {
    return hasGalleryQualityEngagement(s) || s.unique_cloudinary_photos >= DEFAULT_MIN_UNIQUE;
  }
  if (
    s.catalog_size >= 3 &&
    s.coverage_pct >= 60 &&
    s.unique_cloudinary_photos >= 3 &&
    hasGalleryQualityEngagement(s)
  ) {
    return true;
  }
  if (
    (s.total_dwell_seconds >= 20 || s.max_photo_dwell_seconds >= 10) &&
    s.unique_cloudinary_photos >= 2
  ) {
    return true;
  }
  if (s.photos_long_dwell >= 3 && s.unique_cloudinary_photos >= 2) return true;
  if (s.zoom_events >= 1 && (s.total_dwell_seconds >= 8 || s.unique_cloudinary_photos >= 2)) {
    return true;
  }
  if (s.download_intents >= 1 && s.unique_cloudinary_photos >= 2) return true;
  if (s.download_intents >= 2) return true;
  if (s.intent_score >= 55 && (s.unique_cloudinary_photos >= 3 || s.download_intents >= 1)) {
    return true;
  }
  return false;
}

export type GalleryDownloadIntentSource =
  | "button"
  | "right_click"
  | "long_press"
  | "drag"
  | "other";

/**
 * Intento de descargar/guardar foto Cloudinary.
 * Botón UI o save attempt (right-click / long-press) — no entrega el archivo.
 */
export function recordGalleryDownloadIntent(opts?: {
  source?: GalleryDownloadIntentSource;
  src?: string;
  publicId?: string;
}): GalleryEngagementSnapshot {
  const source = opts?.source || "other";
  const publicId =
    opts?.publicId ||
    (opts?.src ? cloudinaryPublicIdFromUrl(opts.src) : null) ||
    null;

  const before = readRaw();
  const wasCandidate = isGalleryPremiumRouteCandidateFromRaw(before);

  const isButton = source === "button";
  const isSaveAttempt =
    source === "right_click" || source === "long_press" || source === "drag";

  const downloadIds = [...(before.download_public_ids || [])];
  if (publicId && !downloadIds.includes(publicId)) {
    downloadIds.push(publicId);
  }

  const next: StoredGalleryMap = {
    ...before,
    download_intents: before.download_intents + 1,
    download_button_clicks: before.download_button_clicks + (isButton ? 1 : 0),
    download_save_attempts: before.download_save_attempts + (isSaveAttempt ? 1 : 0),
    download_public_ids: downloadIds.slice(0, MAX_SAMPLE),
    updated_at: Date.now(),
  };
  writeRaw(next);

  const snapshot = toSnapshot(next);

  if (typeof window !== "undefined") {
    try {
      const posthog = (
        window as unknown as { posthog?: { capture?: (e: string, p?: object) => void } }
      ).posthog;
      posthog?.capture?.("gallery_download_intent", {
        site: "delfuocampo",
        source,
        photo_public_id: publicId,
        download_intents: snapshot.download_intents,
        unique_photos: snapshot.unique_cloudinary_photos,
      });
    } catch {
      /* */
    }
  }

  if (isGalleryPremiumRouteCandidate() && !wasCandidate) {
    requestPurchaseEvalRefresh("gallery_download_threshold");
  } else if (isButton || snapshot.download_intents >= 1) {
    requestPurchaseEvalRefresh("gallery_download_intent");
  }

  return snapshot;
}

/** Payload plano para API / Kimi / Slack. */
export function getGalleryContextForEval(): {
  gallery_photo_opens: number;
  gallery_unique_cloudinary_photos: number;
  gallery_lightbox_swipes: number;
  gallery_reopens: number;
  gallery_dwell_seconds: number;
  gallery_max_photo_dwell_seconds: number;
  gallery_photos_long_dwell: number;
  gallery_zoom_events: number;
  gallery_download_intents: number;
  gallery_download_button_clicks: number;
  gallery_download_save_attempts: number;
  gallery_download_sample_public_ids: string;
  gallery_catalog_size: number;
  gallery_coverage_pct: number;
  gallery_folders_touched: number;
  gallery_folders: string;
  gallery_intent_score: number;
  gallery_intent_label: string;
  gallery_brand_folder: string;
  gallery_sample_public_ids: string;
  gallery_sample_catalog_ids: string;
} {
  const s = getGalleryEngagementSnapshot();
  return {
    gallery_photo_opens: s.photo_opens,
    gallery_unique_cloudinary_photos: s.unique_cloudinary_photos,
    gallery_lightbox_swipes: s.lightbox_swipes,
    gallery_reopens: s.reopens,
    gallery_dwell_seconds: s.total_dwell_seconds,
    gallery_max_photo_dwell_seconds: s.max_photo_dwell_seconds,
    gallery_photos_long_dwell: s.photos_long_dwell,
    gallery_zoom_events: s.zoom_events,
    gallery_download_intents: s.download_intents,
    gallery_download_button_clicks: s.download_button_clicks,
    gallery_download_save_attempts: s.download_save_attempts,
    gallery_download_sample_public_ids: s.download_sample_public_ids.join(",").slice(0, 400),
    gallery_catalog_size: s.catalog_size,
    gallery_coverage_pct: s.coverage_pct,
    gallery_folders_touched: s.folders_touched,
    gallery_folders: s.folder_breakdown
      .map((f) => `${f.folder}:${f.count}`)
      .join("|")
      .slice(0, 320),
    gallery_intent_score: s.intent_score,
    gallery_intent_label: s.intent_label,
    gallery_brand_folder: s.brand_folder,
    gallery_sample_public_ids: s.sample_public_ids.join(",").slice(0, 400),
    gallery_sample_catalog_ids: s.sample_catalog_ids.join(",").slice(0, 400),
  };
}

/** Props para Slack / track-funnel-failure (solo si hubo engagement). */
export function getGalleryEngagementForSlack(): Record<string, string | number> {
  const s = getGalleryEngagementSnapshot();
  if (
    s.photo_opens <= 0 &&
    s.unique_cloudinary_photos <= 0 &&
    s.catalog_size <= 0 &&
    s.total_dwell_seconds <= 0
  ) {
    return {};
  }
  const out: Record<string, string | number> = {
    gallery_photo_opens: s.photo_opens,
    gallery_unique_cloudinary_photos: s.unique_cloudinary_photos,
    gallery_catalog_size: s.catalog_size,
    gallery_coverage_pct: s.coverage_pct,
    gallery_intent_score: s.intent_score,
    gallery_intent_label: s.intent_label,
  };
  if (s.total_dwell_seconds > 0) out.gallery_dwell_seconds = s.total_dwell_seconds;
  if (s.max_photo_dwell_seconds > 0) {
    out.gallery_max_photo_dwell_seconds = s.max_photo_dwell_seconds;
  }
  if (s.photos_long_dwell > 0) out.gallery_photos_long_dwell = s.photos_long_dwell;
  if (s.zoom_events > 0) out.gallery_zoom_events = s.zoom_events;
  if (s.download_intents > 0) out.gallery_download_intents = s.download_intents;
  if (s.download_button_clicks > 0) {
    out.gallery_download_button_clicks = s.download_button_clicks;
  }
  if (s.lightbox_swipes > 0) out.gallery_lightbox_swipes = s.lightbox_swipes;
  if (s.reopens > 0) out.gallery_reopens = s.reopens;
  if (s.folders_touched > 0) {
    out.gallery_folders_touched = s.folders_touched;
    out.gallery_folders = s.folder_breakdown
      .map((f) => `${f.folder}:${f.count}`)
      .join("|")
      .slice(0, 200);
  }
  if (s.brand_folder) out.gallery_brand_folder = s.brand_folder;
  return out;
}
